// Article search utility for finding articles from sitemap

let articlesCache = null;
let lastFetchTime = null;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

/**
 * Fetch and parse sitemap to extract all articles
 */
export async function fetchArticles() {
  // Return cached articles if still valid
  if (articlesCache && lastFetchTime && (Date.now() - lastFetchTime < CACHE_DURATION)) {
    return articlesCache;
  }

  try {
    const response = await fetch('https://www.foodfacts.org/sitemap.xml');
    const xmlText = await response.text();
    
    // Parse XML to extract article URLs
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    const urlElements = xmlDoc.getElementsByTagName('loc');
    
    const articles = [];
    
    for (let i = 0; i < urlElements.length; i++) {
      const url = urlElements[i].textContent;
      
      // Only include actual articles (not categories, tags, or pages)
      if (url.includes('/articles/')) {
        // Extract slug and create readable title
        const slug = url.replace('https://www.foodfacts.org/articles/', '');
        const title = slug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        articles.push({
          url,
          slug,
          title,
          searchText: `${title} ${slug}`.toLowerCase()
        });
      }
    }
    
    console.log(`📚 Loaded ${articles.length} articles from sitemap`);
    
    // Cache the results
    articlesCache = articles;
    lastFetchTime = Date.now();
    
    return articles;
  } catch (error) {
    console.error('❌ Failed to fetch articles from sitemap:', error);
    return [];
  }
}

/**
 * Search articles by query string
 * Returns top 5 matches ranked by relevance
 */
export function searchArticles(query, articles) {
  if (!query || !articles || articles.length === 0) {
    return [];
  }
  
  const searchTerms = query.toLowerCase().split(/\s+/);
  
  // Score each article based on how many search terms match
  const scoredArticles = articles.map(article => {
    let score = 0;
    
    searchTerms.forEach(term => {
      // Exact title match gets highest score
      if (article.title.toLowerCase().includes(term)) {
        score += 10;
      }
      // Slug match gets medium score
      if (article.slug.includes(term)) {
        score += 5;
      }
      // Any match in searchText gets base score
      if (article.searchText.includes(term)) {
        score += 1;
      }
    });
    
    return {
      ...article,
      score
    };
  });
  
  // Filter out articles with no matches and sort by score
  const matches = scoredArticles
    .filter(article => article.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5); // Return top 5 matches
  
  return matches;
}

/**
 * Find single best matching article
 */
export function findArticle(query, articles) {
  const matches = searchArticles(query, articles);
  return matches.length > 0 ? matches[0] : null;
}
